import { Spinner } from "@components/ui-kit";
import { useGetUsersLazyQuery } from "@src/__generated__/graphql";
import type { UserType } from "@src/stores/models/Me";
import { withCan } from "@src/utils/components/permissions";
import { useUpdateURI } from "@src/utils/hooks/useUpdateURI";
import { getParamFromQuery } from "@src/utils/url";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { CardListUsers } from "./CardListUsers";
import { StoreUsers } from "./StoreUsers";

// context holding page related data
export const PageUsersCtx = React.createContext<StoreUsers>(
  null as any as StoreUsers,
);

/**
 * encapsulate initial data fetch & refetch
 */
function useData(store: StoreUsers) {
  const [fetchUsers, { data, loading, error, refetch }] = useGetUsersLazyQuery({
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      store.fromGetUsersQuery(data);
    },
  });

  useEffect(() => {
    fetchUsers({ variables: store.queryParams });
  }, [store.queryParams]);

  store.refetch = refetch;

  return { loading, error, data };
}

type PageUsersProps = {
  userType: UserType;
};

export const PageUsers = withCan(
  { do: ["user_read_all"] },
  observer(function PageUsers({ userType }: PageUsersProps) {
    const { query } = useRouter();

    const [store] = useState(() => {
      const store = StoreUsers.create(userType);
      store.where.fromURLSearchParam(query);
      store.orderBy.fromURLSearchParam(query);
      store.pagination.fromURLSearchParam(query);
      store.searchTerm = getParamFromQuery("search", query) ?? "";
      return store;
    });

    useUpdateURI(store.searchParams);

    const { loading } = useData(store);

    return (
      <PageUsersCtx.Provider value={store}>
        <div className="flex justify-center items-center">
          <div className="w-full">
            {loading && <Spinner absolute />}
            <CardListUsers />
          </div>
        </div>
      </PageUsersCtx.Provider>
    );
  }),
);
